/**
 * Schema-facing contract for the entity-preview-card display component
 * (`ui2:entityPreviewCard`). DW, MW, and NM all import from here so
 * the prop surface stays identical across platforms. Platform-only
 * concerns (RN TouchableHighlight handlers, web FramesManager, layout
 * style props) extend this shape inside each platform's component file.
 */

import { UI2IconName } from '../../icons/ui2-icon-names'

export enum EntityPreviewCardCellType {
  INFO = 'info',
  IDENTIFIER = 'identifier',
  /**
   * Expands a `path`- or `formula`-resolved array of objects into one
   * INFO cell per unique label, values joined by ', '. `labelPath` and
   * `valuePath` pick which keys on each object supply the label and
   * value; both default to 'label' and 'value' respectively.
   */
  INFO_FROM_ARRAY = 'info_from_array',
  STATUS = 'status',
  NOTES = 'notes',
}

export interface EntityPreviewCardCellProps {
  path?: string
  formula?: string
  value?: any
  label?: string
  color?: string
  /** Only used when `type === INFO_FROM_ARRAY`. Defaults to 'label'. */
  labelPath?: string
  /** Only used when `type === INFO_FROM_ARRAY`. Defaults to 'value'. */
  valuePath?: string
  type: EntityPreviewCardCellType
}

/**
 * Interaction is determined by two orthogonal booleans (`isDisabled`,
 * `isReadOnly`) and the two schema paths (`entityEditSchemaPath`,
 * `entityReadOnlySchemaPath`). Click/tap opens the appropriate
 * sheet/modal — schema authors never wire a custom handler. The full
 * truth table lives at the top of each platform's component file.
 */
export interface EntityPreviewCardProps {
  entity?: any
  /** Needed when used in a list context so the renderer can determine the value. */
  value?: any
  title?: string
  data?: EntityPreviewCardCellProps[]

  /**
   * UI subschema path opened when the viewer can edit the entity
   * (default when the viewer's firm owns the entity, `isDisabled` is
   * false, `isReadOnly` is false). Platform default:
   * `'uiSchema.shared.entityEditSchema'`.
   *
   * If nothing resolves at this path the edit action is disabled: NM and
   * MW fall through to `entityReadOnlySchemaPath`, and render the card
   * muted and untappable when that is unset too. DW is the exception —
   * it keeps its fallback of opening `/entity/<id>` in a new tab, an
   * affordance neither mobile surface has.
   */
  entityEditSchemaPath?: string
  /**
   * UI subschema path opened when the viewer cannot edit the entity —
   * either because `isReadOnly` was set explicitly, or because the
   * entity belongs to a different firm than the viewer. If unset, no
   * sheet opens in read-only cases (the card renders muted and
   * non-interactive).
   */
  entityReadOnlySchemaPath?: string

  /**
   * Extra renderer scope for the sheet this card opens. Normally supplied by
   * the host field (`ui2:entitySearchField`) rather than set on the card
   * directly. See `extraState` there.
   */
  extraState?: { [key: string]: any }

  /** Renders the entity type label above the title. Default: false. */
  showEntityType?: boolean
  /**
   * Controls the left type-stripe. Defaults to `true`. Result-list
   * containers opt out via `EntityPreviewCardContext`. An explicit prop
   * wins over the context.
   *
   * The stripe is a transparent column holding a primary-orange icon —
   * it does not colour-code by entity type. The icon alone carries the
   * type, so a column of cards reads as one surface.
   */
  showTypeStripe?: boolean
  /**
   * Icon name to render in the type stripe. When omitted the stripe
   * falls back to the entity-schema display icon. Only the icon is taken
   * from that registry entry; its colour is deliberately ignored.
   */
  typeStripeIconName?: UI2IconName

  /**
   * Renders the card as muted (subtle lighter bg, no shadow, muted
   * border) and blocks all click/tap interaction. Overrides
   * `isReadOnly` — a disabled card is never openable, regardless of
   * schema config.
   */
  isDisabled?: boolean
  /**
   * Renders the card normally (interactive-looking) but routes
   * click/tap to `entityReadOnlySchemaPath` instead of
   * `entityEditSchemaPath`. If `entityReadOnlySchemaPath` is unset,
   * the card renders muted and non-interactive (same visual as
   * `isDisabled`).
   */
  isReadOnly?: boolean
  /**
   * Renders a compact single-line chip: entity-type icon (smaller) +
   * display name (ellipsized). All identifier, info, and status rows
   * are suppressed. Interaction (open edit/read-only sheet) resolves
   * the same way as a full card via `isDisabled`, `isReadOnly`, and
   * the schema paths. Defaults `showTypeStripe` to `true`.
   */
  isChipMode?: boolean
}
